import type { ApplicationContext, ErrorCode } from '@hc-frontend/core-context';
import type { ZipCode } from '@hc-frontend/core-types';
import type { Location } from '@hc-frontend/core-types';
import type { IOperationResult } from '@hc-frontend/core-utils-business-operation';
import { createErrorOperationResult } from '@hc-frontend/core-utils-business-operation';
import { find, isNil, reject, whereEq } from 'ramda';

import { UpdateProspectLocationParameters } from '../../entities';
import type {
  UpdateProspectLocationRepository,
  UpdateProspectLocationResult,
} from '../../repositories';

type GetLocationResult = IOperationResult<
  Location[],
  ErrorCode<'failedToGetLocationByZip'>
>;

export type UpdateProspectLocationUseCase = (
  parameters: UpdateProspectLocationParameters,
) => Promise<UpdateProspectLocationResult>;

export type UpdateProspectLocationFactoryParams = Pick<
  ApplicationContext,
  'log'
> & {
  updateProspectLocation: UpdateProspectLocationRepository;
  getLocationByZip: (zip: ZipCode) => Promise<GetLocationResult>;
};

export function updateProspectLocationFactory({
  getLocationByZip,
  updateProspectLocation,
  log,
}: UpdateProspectLocationFactoryParams): UpdateProspectLocationUseCase {
  return async (parameters) => {
    const parsedParams = UpdateProspectLocationParameters.safeParse(parameters);

    if (!parsedParams.success) {
      log({
        type: 'error',
        context: 'server',
        message: 'failedToUpdateProspectLocation',
        data: {
          rootCause: parsedParams.error,
        },
      });
      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToUpdateProspectLocation',
            rootCause: parsedParams.error,
          },
        ],
      });
    }

    const locationByZipResponse = await getLocationByZip(
      parsedParams.data.zipCode,
    );

    if (!locationByZipResponse.success) {
      log({
        context: 'server',
        type: 'error',
        message: locationByZipResponse.errors[0].code,
        data: {
          rootCause: locationByZipResponse.errors[0].rootCause,
        },
      });
      return locationByZipResponse;
    }

    const locationByZipData = locationByZipResponse.data;

    const location = find(
      whereEq(
        reject(isNil, {
          zipCode: parsedParams.data.zipCode,
          county: parsedParams.data.county,
          city: parsedParams.data.city,
          stateAcronym: parsedParams.data.stateAcronym,
          state: parsedParams.data.state,
        }),
      ),
      locationByZipData,
    );

    if (!location) {
      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToGetLocationByZip',
            rootCause: new Error('Could not found any valid location'),
          },
        ],
      });
    }

    const updateProspectResult = await updateProspectLocation({
      ...location,
      userId: parsedParams.data.userId,
      sessionId: parsedParams.data.sessionId,
      domainSessionId: parsedParams.data.domainSessionId,
      domainUserId: parsedParams.data.domainUserId,
    });

    if (!updateProspectResult.success) {
      log({
        context: 'server',
        type: 'error',
        message: updateProspectResult.errors[0].code,
        data: {
          rootCause: updateProspectResult.errors[0].rootCause,
        },
      });
    }

    return updateProspectResult;
  };
}
